import './OverflowMenu.scss';
import { autoUpdate, useFloating } from '@floating-ui/react';
import clsx from 'clsx';
import React, { memo, ReactNode, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { useOutsideClick } from 'src/modules/common/hooks/useOutsideClick';
import { RuntimeContext } from 'src/runtimeContext';

type Props = {
  readonly id: string;
  readonly buttonTooltip: React.ReactNode;
  readonly interactive?: boolean;
  readonly className?: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
  readonly children: ReactNode;
  readonly opened: boolean;
  readonly setOpened: (state: boolean) => void;
};

export const OverflowMenu = memo(({
  id,
  buttonTooltip,
  className,
  interactive = true,
  disabled,
  label,
  children,
  opened,
  setOpened,
}: Props) => {
  const { modalRoot } = useContext(RuntimeContext);

  const { x, y, strategy, refs } = useFloating({
    open: opened,
    onOpenChange: setOpened,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  });

  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick(ref, () => setOpened(false));

  return (
    <div
      ref={ref}
      className={clsx({
        'bp-overflow-menu': true,
        'bp-overflow-menu--opened': opened,
        'bp-overflow-menu--interactive': interactive,
      }, className)}
    >
      <Tooltip customRef={refs.setReference} id={id} value={buttonTooltip} disabled={disabled}>
        {label}
      </Tooltip>

      {opened ? createPortal(
        <div
          ref={refs.setFloating}
          className="bp-overflow-menu__list"
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          data-testid="overflow-menu-list"
        >
          {children}
        </div>,
        modalRoot,
      ) : null}
    </div>
  );
});
